import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Mail, 
  Plus, 
  Search, 
  Send,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Users,
  TrendingUp,
  MousePointerClick,
  BarChart3,
  FileText,
  SendHorizonal
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { marketingAPI, EmailCampaign, EmailTemplate, Lead } from '../../lib/marketing-api';
import { toast } from 'sonner';
import { logger } from '../../services/logger.service';

export function EmailCampaigns() {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkSendDialog, setShowBulkSendDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<EmailCampaign | null>(null);
  const [previewCampaign, setPreviewCampaign] = useState<EmailCampaign | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    status: 'draft' as EmailCampaign['status'],
    segment: '',
    scheduledDate: '',
    templateId: '' // New field for template selection
  });

  const [bulkSendData, setbulkSendData] = useState({
    templateId: '',
    segment: 'all',
    leadStatus: 'all'
  });

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    loadLeads();
  }, []);

  const loadCampaigns = async () => {
    try {
      setIsLoading(true);
      const { campaigns: data } = await marketingAPI.getEmailCampaigns();
      setCampaigns(data);
    } catch (error) {
      logger.error('Failed to load email campaigns', error as Error, undefined, 'EmailCampaigns');
      toast.error('Failed to load email campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { templates: data } = await marketingAPI.getEmailTemplates();
      setTemplates(data);
    } catch (error) {
      logger.error('Failed to load templates', error as Error, undefined, 'EmailCampaigns');
    }
  };

  const loadLeads = async () => {
    try {
      const { leads: data } = await marketingAPI.getLeads();
      setLeads(data);
    } catch (error) {
      logger.error('Failed to load leads', error as Error, undefined, 'EmailCampaigns');
    }
  };

  const handleUseTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        subject: template.subject,
        content: template.content,
        templateId
      });
      toast.success('Template loaded successfully');
    }
  };

  const handleCreateCampaign = async () => {
    try {
      await marketingAPI.createEmailCampaign(formData);
      toast.success('Email campaign created successfully');
      setShowAddDialog(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to create campaign', error as Error, undefined, 'EmailCampaigns');
      toast.error('Failed to create campaign');
    }
  };

  const handleUpdateCampaign = async () => {
    if (!editingCampaign) return;
    
    try {
      await marketingAPI.updateEmailCampaign(editingCampaign.id, formData);
      toast.success('Campaign updated successfully');
      setEditingCampaign(null);
      resetForm();
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to update campaign', error as Error, undefined, 'EmailCampaigns');
      toast.error('Failed to update campaign');
    }
  };

  const handleSendCampaign = async (campaign: EmailCampaign) => {
    if (!confirm(`Send "${campaign.name}" to all recipients?`)) return;
    
    try {
      // Simulate sending
      const randomSent = Math.floor(Math.random() * 500) + 100;
      const randomOpen = Math.floor(Math.random() * 50) + 20;
      const randomClick = Math.floor(Math.random() * 20) + 5;
      
      await marketingAPI.updateEmailCampaign(campaign.id, {
        status: 'active',
        sentCount: randomSent,
        openRate: randomOpen,
        clickRate: randomClick
      });
      toast.success('Campaign sent successfully!');
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to send campaign', error as Error, undefined, 'EmailCampaigns');
      toast.error('Failed to send campaign');
    }
  };

  const handleDeleteCampaign = async (campaign: EmailCampaign) => {
    if (!confirm(`Delete campaign "${campaign.name}"? This action cannot be undone.`)) return;

    try {
      await marketingAPI.deleteEmailCampaign(campaign.id);
      toast.success('Campaign deleted successfully');
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to delete campaign', error as Error, undefined, 'EmailCampaigns');
      toast.error('Failed to delete campaign');
    }
  };

  const handleBulkSend = async () => {
    const template = templates.find(t => t.id === bulkSendData.templateId);
    if (!template) {
      toast.error('Please select a template');
      return;
    }

    let targetLeads = [...leads];
    if (bulkSendData.leadStatus !== 'all') {
      targetLeads = targetLeads.filter(l => l.status === bulkSendData.leadStatus);
    }

    if (targetLeads.length === 0) {
      toast.error('No matching leads found');
      return;
    }

    try {
      const campaignData = {
        name: `Bulk Send - ${template.name}`,
        subject: template.subject,
        content: template.content,
        templateId: template.id,
        status: 'active' as EmailCampaign['status'],
        sentCount: targetLeads.length,
        openRate: 0,
        clickRate: 0,
      };
      await marketingAPI.createEmailCampaign(campaignData);
      toast.success(`Bulk email sent to ${targetLeads.length} leads using "${template.name}" template`);
      setShowBulkSendDialog(false);
      setbulkSendData({ templateId: '', segment: 'all', leadStatus: 'all' });
      loadCampaigns();
    } catch (error) {
      logger.error('Failed to send bulk email', error as Error, undefined, 'EmailCampaigns');
      toast.error('Failed to send bulk email');
    }
  };

  const openEditDialog = (campaign: EmailCampaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      subject: campaign.subject,
      content: campaign.content,
      status: campaign.status,
      segment: campaign.segment || '',
      scheduledDate: campaign.scheduledDate || '',
      templateId: campaign.templateId || ''
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      content: '',
      status: 'draft',
      segment: '',
      scheduledDate: '',
      templateId: ''
    });
  };

  const getStatusBadge = (status: EmailCampaign['status']) => {
    const variants: Record<EmailCampaign['status'], string> = {
      draft: 'bg-gray-500',
      scheduled: 'bg-blue-500',
      active: 'bg-green-500',
      paused: 'bg-yellow-500',
      completed: 'bg-purple-500'
    };
    
    return (
      <Badge className={variants[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    campaign.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalSent = campaigns.reduce((sum, c) => sum + c.sentCount, 0);
  const avgOpenRate = campaigns.length > 0
    ? (campaigns.reduce((sum, c) => sum + c.openRate, 0) / campaigns.length).toFixed(1)
    : 0;
  const avgClickRate = campaigns.length > 0
    ? (campaigns.reduce((sum, c) => sum + c.clickRate, 0) / campaigns.length).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Email Campaigns
          </h2>
          <p className="text-muted-foreground mt-1">
            Create and manage email marketing campaigns
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBulkSendDialog(true)} className="gap-2" data-testid="marketing-campaign-bulk-send-button">
            <SendHorizonal className="w-4 h-4" />
            Bulk Send
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="marketing-campaign-new-button">
            <Plus className="w-4 h-4" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
                <p className="text-2xl font-bold">{campaigns.length}</p>
              </div>
              <Mail className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sent</p>
                <p className="text-2xl font-bold">{totalSent.toLocaleString()}</p>
              </div>
              <Send className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Open Rate</p>
                <p className="text-2xl font-bold">{avgOpenRate}%</p>
              </div>
              <Eye className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Click Rate</p>
                <p className="text-2xl font-bold">{avgClickRate}%</p>
              </div>
              <MousePointerClick className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search campaigns by name or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <CardTitle>All Campaigns ({filteredCampaigns.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              Loading campaigns...
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No campaigns found. Create your first email campaign!
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.map((campaign) => (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{campaign.name}</h3>
                        {getStatusBadge(campaign.status)}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Subject: <span className="font-medium text-foreground">{campaign.subject}</span>
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Send className="w-4 h-4" />
                            <span>Sent</span>
                          </div>
                          <p className="font-semibold">{campaign.sentCount.toLocaleString()}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Eye className="w-4 h-4" />
                            <span>Open Rate</span>
                          </div>
                          <p className="font-semibold text-green-600">{campaign.openRate}%</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <MousePointerClick className="w-4 h-4" />
                            <span>Click Rate</span>
                          </div>
                          <p className="font-semibold text-blue-600">{campaign.clickRate}%</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span>Created</span>
                          </div>
                          <p className="font-semibold">
                            {new Date(campaign.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewCampaign(campaign)}
                        title="Preview"
                        data-testid="marketing-campaign-preview-button"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(campaign)}
                        title="Edit"
                        data-testid="marketing-campaign-edit-button"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {campaign.status === 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendCampaign(campaign)}
                          className="text-green-600 hover:bg-green-500/10"
                          title="Send"
                          data-testid="marketing-campaign-send-button"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteCampaign(campaign)}
                        className="text-destructive hover:bg-destructive/10"
                        title="Delete"
                        data-testid="marketing-campaign-delete-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Campaign Dialog */}
      <Dialog open={showAddDialog || editingCampaign !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setEditingCampaign(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create Email Campaign'}
            </DialogTitle>
            <DialogDescription>
              {editingCampaign ? 'Update campaign details' : 'Design a new email marketing campaign'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Campaign Name *</Label>
              <Input
                id="campaign-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., New Property Launch Announcement"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject">Email Subject *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Exclusive New Properties in Sandton"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Email Content *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your email content here..."
                rows={10}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: EmailCampaign['status']) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="segment">Target Segment (Optional)</Label>
                <Input
                  id="segment"
                  value={formData.segment}
                  onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                  placeholder="e.g., High-value leads"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="scheduledDate">Schedule Date (Optional)</Label>
              <Input
                id="scheduledDate"
                type="datetime-local"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateId">Use Template (Optional)</Label>
              <Select
                value={formData.templateId}
                onValueChange={(value: string) => handleUseTemplate(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setEditingCampaign(null);
                resetForm();
              }}
              data-testid="marketing-campaign-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={editingCampaign ? handleUpdateCampaign : handleCreateCampaign} data-testid="marketing-campaign-save-button">
              {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewCampaign !== null} onOpenChange={(open) => {
        if (!open) setPreviewCampaign(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Preview</DialogTitle>
            <DialogDescription>
              Preview how your email will look to recipients
            </DialogDescription>
          </DialogHeader>
          {previewCampaign && (
            <div className="space-y-4">
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">{previewCampaign.name}</h3>
                <p className="text-sm text-muted-foreground">
                  <strong>Subject:</strong> {previewCampaign.subject}
                </p>
              </div>
              <div className="bg-muted/30 p-6 rounded-lg">
                <div className="whitespace-pre-wrap">{previewCampaign.content}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPreviewCampaign(null)} data-testid="marketing-campaign-preview-close-button">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Dialog */}
      <Dialog open={showBulkSendDialog} onOpenChange={setShowBulkSendDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SendHorizonal className="w-5 h-5 text-primary" />
              Bulk Send Email
            </DialogTitle>
            <DialogDescription>
              Send a template-based email to multiple leads at once
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Email Template *</Label>
              <Select
                value={bulkSendData.templateId}
                onValueChange={(value) => setbulkSendData({ ...bulkSendData, templateId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template to send" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex flex-col">
                        <span>{template.name}</span>
                        <span className="text-xs text-muted-foreground">{template.subject}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Filter by Lead Status</Label>
              <Select
                value={bulkSendData.leadStatus}
                onValueChange={(value) => setbulkSendData({ ...bulkSendData, leadStatus: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leads</SelectItem>
                  <SelectItem value="new">New Leads</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="proposal">Proposal Sent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-muted/30 rounded-lg p-3 text-sm">
              <p className="font-medium">Estimated recipients:</p>
              <p className="text-2xl font-bold text-primary">
                {bulkSendData.leadStatus === 'all'
                  ? leads.length
                  : leads.filter(l => l.status === bulkSendData.leadStatus).length}
              </p>
              <p className="text-muted-foreground">leads matching criteria</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkSendDialog(false)} data-testid="marketing-campaign-bulk-cancel-button">
              Cancel
            </Button>
            <Button
              onClick={handleBulkSend}
              disabled={!bulkSendData.templateId}
              className="gap-2"
              data-testid="marketing-campaign-bulk-send-confirm-button"
            >
              <SendHorizonal className="w-4 h-4" />
              Send to All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}